var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { createHawkinsDataAttributeName, isScrolledToBottom, isScrolledToTop, isVisibleInScrollContainer, KeyAttributeValue, useArrowKeyNavigation, useIsomorphicLayoutEffect, } from '@hawkins/helpers';
import { IconArrowDown } from '@hawkins/icons/lib/iconArrowDown';
import { IconArrowUp } from '@hawkins/icons/lib/iconArrowUp';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { Button } from '../button';
import { TimePickerTestIds } from './timePicker.testids';
var displayName = 'TimePickerOptionsSection';
var optionValueAttr = createHawkinsDataAttributeName('optionValue', { namespace: displayName });
function autoScrollToSelectedOption(_a) {
    var containerRef = _a.containerRef, isScrollable = _a.isScrollable, selectedOption = _a.selectedOption;
    if (!isScrollable || selectedOption === null)
        return;
    var container = containerRef.current;
    var selectedEl = container === null || container === void 0 ? void 0 : container.querySelector("[".concat(optionValueAttr, "=\"").concat(selectedOption, "\"]"));
    if (!container || !(selectedEl instanceof HTMLElement))
        return;
    // Auto-scroll to the selected value.
    container.scrollTo(0, selectedEl.offsetTop);
}
/**
 * @private
 */
export function TimePickerOptionsSection(_a) {
    var _b, _c;
    var classes = _a.classes, disabled = _a.disabled, formatOptionLabel = _a.formatOptionLabel, onChange = _a.onChange, options = _a.options, optionType = _a.optionType, selectedOption = _a.selectedOption, translate = _a.translate;
    // Props
    var numOptions = options.length;
    // Refs
    var prevScrollTopRef = React.useRef(0);
    var scrollTimeoutIdRef = React.useRef();
    var wasOptionSelectedRef = React.useRef(false);
    var _d = useArrowKeyNavigation({ supportUpDownKeys: true }), containerCallbacks = _d.containerCallbacks, containerRef = _d.containerRef;
    var clearScrollTimeout = React.useCallback(function () {
        clearTimeout(scrollTimeoutIdRef.current);
    }, []);
    var continuouslyScroll = React.useCallback(function (_a) {
        var _b;
        var direction = _a.direction;
        clearScrollTimeout();
        (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.scrollBy(0, 1 * (direction === 'up' ? -1 : 1));
        scrollTimeoutIdRef.current = setTimeout(function () {
            continuouslyScroll({ direction: direction });
        }, 0);
    }, [clearScrollTimeout, containerRef]);
    var continuouslyScrollDown = React.useCallback(function () {
        continuouslyScroll({ direction: 'down' });
    }, [continuouslyScroll]);
    var continuouslyScrollUp = React.useCallback(function () {
        continuouslyScroll({ direction: 'up' });
    }, [continuouslyScroll]);
    var handleChange = React.useCallback(function (option) {
        // The effect that reads and resets `wasOptionSelectedRef` only runs when `selectedOption` changes,
        // so only update `wasOptionSelectedRef` if the selected option is different
        // (otherwise, `wasOptionSelectedRef` won't get reset correctly).
        if (option !== selectedOption) {
            wasOptionSelectedRef.current = true;
        }
        onChange(option);
    }, [onChange, selectedOption]);
    // State
    var _e = __read(React.useState(null), 2), initialSelectedOption = _e[0], setInitialSelectedOption = _e[1];
    var _f = __read(React.useState(null), 2), isScrollable = _f[0], setIsScrollable = _f[1];
    // Determine the initially selected option.
    useIsomorphicLayoutEffect(function () {
        if (selectedOption !== null)
            setInitialSelectedOption(selectedOption);
        // We only care about the initially selected option,
        // and we don't want this value changing as the user selects different values.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Trigger a recalculation of the container scrollability whenever the number of options changes.
    useIsomorphicLayoutEffect(function () {
        setIsScrollable(null);
    }, [numOptions]);
    // Determine whether the options container is scrollable.
    useIsomorphicLayoutEffect(function () {
        if (isScrollable !== null)
            return;
        var container = containerRef.current;
        if (!container)
            return;
        var clientHeight = container.clientHeight, scrollHeight = container.scrollHeight, scrollTop = container.scrollTop;
        prevScrollTopRef.current = scrollTop;
        if (scrollHeight > clientHeight) {
            // Make sure the user has room to scroll in either direction
            // (otherwise, no scroll event will fire).
            if (isScrolledToTop(container)) {
                container.scrollTo(0, 1);
            }
            else if (isScrolledToBottom(container)) {
                container.scrollTo(0, scrollHeight - clientHeight - 1);
            }
            setIsScrollable(true);
        }
        else {
            setIsScrollable(false);
        }
    }, [containerRef, isScrollable]);
    useIsomorphicLayoutEffect(function () {
        autoScrollToSelectedOption({ containerRef: containerRef, isScrollable: isScrollable, selectedOption: selectedOption });
        // We only want this effect to run once (i.e., after we figure out that the container is scrollable).
        // We don't want to auto-scroll every time the selected option changes.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScrollable]);
    useIsomorphicLayoutEffect(function () {
        // Don't auto-scroll if an option was selected
        // (i.e., only auto-scroll if the time was updated from outside the <TimePicker>).
        // Otherwise, the user could experience an awkward scroll jump while selecting an option.
        if (wasOptionSelectedRef.current) {
            wasOptionSelectedRef.current = false;
            return;
        }
        autoScrollToSelectedOption({ containerRef: containerRef, isScrollable: isScrollable, selectedOption: selectedOption });
        // We don't include `isScrollable` in this dependency array,
        // because it's already handled by the other auto-scroll layout effect above.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);
    // Set event listeners that facilitate infinite scrolling up and down.
    React.useEffect(function () {
        var manageScrollPosition = function () {
            var container = containerRef.current;
            if (!container)
                return;
            var clientHeight = container.clientHeight, scrollHeight = container.scrollHeight, scrollTop = container.scrollTop;
            var isScrollingUp = scrollTop < prevScrollTopRef.current;
            prevScrollTopRef.current = scrollTop;
            if (isScrollingUp) {
                if (isScrolledToTop(container))
                    container.scrollTo(0, scrollHeight / 2);
            }
            else if (isScrolledToBottom(container)) {
                container.scrollTo(0, scrollHeight / 2 - clientHeight);
            }
            else if (typeof document !== 'undefined') {
                var activeElement = document.activeElement;
                // Because only the first set of options inside our infinite scroll container is focusable,
                // there will be cases in which the focused element is not visible
                // (specifically, when using the down arrow key to move focus from the last option to the first option).
                // When that happens, we must correct the scroll position.
                if (activeElement instanceof HTMLElement &&
                    container.contains(activeElement) &&
                    !isVisibleInScrollContainer({ container: container, el: activeElement }) &&
                    // Scrolling to the very top will cause an infinite loop. This should never happen
                    // (since our CSS styles add a 1-pixel top margin to the first option inside a scrollable container),
                    // but check for it just to be safe.
                    activeElement.offsetTop > 0) {
                    container.scrollTo(0, activeElement.offsetTop);
                }
            }
        };
        var container = containerRef.current;
        container === null || container === void 0 ? void 0 : container.addEventListener('scroll', manageScrollPosition);
        return function () {
            clearTimeout(scrollTimeoutIdRef.current);
            container === null || container === void 0 ? void 0 : container.removeEventListener('scroll', manageScrollPosition);
        };
    }, [containerRef]);
    var optionsContainerClassName = clsx(classes.optionsContainer, (_b = {},
        _b[classes.optionsContainerScrollable] = isScrollable,
        _b));
    var scrollButtonClassName = clsx(classes.scrollButton, (_c = {},
        // Even if one options container isn't scrollable, the others could be,
        // so we want the scroll button to take up space but be invisible.
        _c[classes.scrollButtonHidden] = !isScrollable,
        _c));
    return (_jsxs("div", __assign({ className: classes.optionsSection }, { children: [_jsx(Button
            // Keep this <Button> non-focusable and out of the accessibility tree.
            // I don't think screen reader or keyboard users need to access the scroll buttons.
            , { "aria-hidden": true, "aria-label": translate("timePicker.".concat(optionType, ".scrollUpBtnLabel")), className: scrollButtonClassName, disabled: disabled, icon: IconArrowUp, iconProps: { size: 'small' }, onMouseDown: continuouslyScrollUp, onMouseLeave: clearScrollTimeout, onMouseUp: clearScrollTimeout, onTouchEnd: clearScrollTimeout, onTouchStart: continuouslyScrollUp, tabIndex: -1, variant: "icon" }), _jsx("div", __assign({ "aria-label": translate("timePicker.".concat(optionType, ".groupLabel")), className: optionsContainerClassName, "data-testid": TimePickerTestIds["".concat(optionType.toUpperCase(), "_CONTAINER")], ref: containerRef, 
                // HACK(a11y): Visually, this component looks like a spinbutton (see https://www.w3.org/TR/wai-aria-practices-1.1/#spinbutton),
                // but functionally, it behaves more like a radiogroup, so we use radiogroup conventions to implement it.
                // There may be a better/more accessible way to design/build this component.
                role: "radiogroup" }, containerCallbacks, { children: (isScrollable
                    ? // Display the options set twice so we can implement
                        // infinite scrolling in either direction.
                        [options, options]
                    : [
                        __spreadArray(__spreadArray([], __read(options.filter(function (option) { return option === initialSelectedOption; })), false), __read(options.filter(function (option) { return option !== initialSelectedOption; })), false),
                    ]).reduce(function (result, optionSet, optionSetIdx) {
                    optionSet.forEach(function (option, optionIdx) {
                        var _a, _b;
                        var isSelected = option === selectedOption;
                        result.push(_jsx("div", __assign({ className: clsx(HawkinsGlobalClass.FOCUSABLE, classes.option, (_a = {},
                                _a[classes.optionDisabled] = disabled,
                                _a[classes.optionEnabled] = !disabled,
                                _a[classes.optionSelected] = isSelected,
                                _a)), onClick: disabled ? undefined : function () { return handleChange(option); } }, (optionSetIdx === 0
                            ? (_b = {},
                                // Only support auto-scrolling within the first option set.
                                _b[optionValueAttr] = option,
                                _b['aria-checked'] = isSelected,
                                _b['aria-disabled'] = disabled || undefined,
                                _b.onKeyDown = disabled
                                    ? undefined
                                    : function (ev) {
                                        if (ev.key === KeyAttributeValue.SPACE) {
                                            // Prevent a spacebar press from scrolling the page.
                                            ev.preventDefault();
                                            handleChange(option);
                                        }
                                    },
                                _b.role = 'radio',
                                // Make only the selected option tabbable.
                                // If no option is selected, then make the first option tabbable.
                                _b.tabIndex = isSelected || (selectedOption === null && optionIdx === 0) ? 0 : -1,
                                _b) : { 'aria-hidden': true }), { children: formatOptionLabel ? formatOptionLabel(option) : option }), "".concat(option, "_").concat(optionSetIdx)));
                    });
                    return result;
                }, []) })), _jsx(Button
            // Keep this <div> non-focusable and out of the accessibility tree.
            // I don't think screen reader or keyboard users need to access the scroll buttons.
            , { "aria-hidden": true, "aria-label": translate("timePicker.".concat(optionType, ".scrollDownBtnLabel")), className: scrollButtonClassName, disabled: disabled, icon: IconArrowDown, iconProps: { size: 'small' }, onMouseDown: continuouslyScrollDown, onMouseLeave: clearScrollTimeout, onMouseUp: clearScrollTimeout, onTouchEnd: clearScrollTimeout, onTouchStart: continuouslyScrollDown, tabIndex: -1, variant: "icon" })] })));
}
TimePickerOptionsSection.displayName = displayName;
